<template>
  <div>
    <v-toolbar
      style="padding-top:15px;"
      color="white"
      flat
      dense
    >
      <v-autocomplete
        v-model="newFormTemplate"
        :loading="searching"
        :items="formTemplates"
        :search-input.sync="search"
        item-value="id"
        item-text="name"
        label="Add Form Template by Name"
        dense
        auto-select-first
        hide-details
      >
        <template slot="no-data">
          <v-list-tile>
            <v-list-tile-title v-if="!searching && !search">
              Type at least
              <strong>3</strong>
              characters to begin searching
            </v-list-tile-title>
            <v-list-tile-title v-if="!searching && search">
              No results for <i>{{search}}</i>. Refine your search and try again.
            </v-list-tile-title>
            <v-list-tile-title v-if="searching">
              <b><v-icon small>search</v-icon>Searching...</b>
            </v-list-tile-title>
          </v-list-tile>
        </template>
      </v-autocomplete>
      <v-btn
        :disabled="!newFormTemplate"
        color="blue"
        flat
        @click="propDialog = true"
      >
        <v-icon>add</v-icon> Add Form Template
      </v-btn>
    </v-toolbar>
    <v-data-table
      :items="contact.userforms"
      :headers="headers"
      :loading="loading"
      disable-initial-sort
    >
      <template slot="no-data">
        <tr>
          <td colspan="5">
            <div style="text-align:center;padding:25px;">
              <v-icon size="125px">find_in_page</v-icon>
              <h3>No Forms Assigned</h3>
            </div>
          </td>
        </tr>
      </template>
      <template
        slot="items"
        slot-scope="props"
      >
        <tr
          :style="(props.item.tags.length > 0) ? 'border-bottom:0;' : null"
        >
          <td>
            <router-link :to="{ name: 'UserForm', params: { id: props.item.id } }">
              <b>{{ props.item.template.name }}</b>
            </router-link>
            <span v-if="props.item.party_name || props.item.form_number"><br /><small>{{props.item.party_name}}&nbsp;<i>{{props.item.form_number}}</i></small></span>
          </td>
          <td>
          </td>
          <td>
          </td>
          <td style="text-transform: capitalize;">
            {{ props.item.status }}
          </td>
          <td>
            <v-icon
              small
              @click="editProps(props.item)"
            >
              edit
            </v-icon>
          </td>
        </tr>
        <tr
          v-if="props.item.tags.length > 0"
        >
          <td colspan="5">
          </td>
        </tr>
      </template>
    </v-data-table>

    <v-dialog
      width="650"
      v-model="propDialog"
    >
      <v-card>
        <v-toolbar
          class="primary-color"
          dark
          flat
          dense
        >
          Add Form Template to {{contact.first_name}} {{contact.last_name}}
        </v-toolbar>
        <v-card-text>
          <div v-if="!editingTemplate.id">
            <h3>You are assigning a new template.</h3>
            The Party Name and Form Number have been prepopulated with {{contact.first_name}}'s information.<br />
            <i>If this is for another party, please update the information.</i>
          </div>
          <div v-if="editingTemplate.id">
            <h3>You are editing an existing templates properties.</h3>
          </div>
          <br />
          <v-text-field
            v-model="props.party_name"
            persistent-hint
            solo
            hint="Party Name - The Party's Name that this Form will be assigned to; ex. Injured Party"
          />
          <v-text-field
            v-model="props.form_number"
            persistent-hint
            solo
            hint="Form Number - An identifier for this Form related to the Party"
          />
          <br />
          <v-combobox
            v-model="editingTemplate.tags"
            :items="tagResults"
            :search-input.sync="tagSearch"
            :disabled="copyTags"
            multiple
            persistent-hint
            hint="Form Tags"
            item-text="tag"
            item-value="tag"
            :return-object="false"
            solo
            :solo-inverted="copyTags"
            small-chips
            hide-selected
            auto-select-first
            @input="tagSearch = ''"
          />
          <v-switch
            v-if="!editingTemplate.id"
            v-model="copyTags"
            :label="`Copy ${contact.full_name}'s Tags on Form`"
          ></v-switch>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            :disabled="saving"
            flat
            color="gray"
            @click="propDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            :disabled="saving"
            flat
            color="blue"
            @click="editingTemplate.id ? updateUserForm() : addUserForm()"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from '@/api'

export default {
  name: 'ContactTemplates',
  props: [ 'contact', 'loading' ],
  data () {
    return {
      formTemplates: [],
      searching: false,
      newFormTemplate: '',
      headers: [
        { text: 'Template Name', value: 'name', sortable: false },
        { text: 'Issued', value: 'created_at' },
        { text: 'Updated', value: 'updated_at' },
        { text: 'Status', value: 'status' },
        { text: 'Actions', width: 100, sortable: false }
      ],
      search: '',
      saving: false,
      propDialog: false,
      props: {
        party_name: '',
        form_number: ''
      },
      editingTemplate: {},
      tagSearch: null,
      tagResults: [],
      copyTags: false
    }
  },
  watch: {
    search () {
      if (this.search) {
        this.loadFormTemplates()
      }
    },
    tagSearch () {
      if (this.tagSearch) {
        this.fetchTags()
      }
    },
    propDialog () {
      if (this.propDialog) {
        if (!this.editingTemplate.id) {
          this.props = {
            party_name: this.contact.full_name,
            form_number: this.contact.cuid
          }
        }
      }
    },
    copyTags () {
      if (this.copyTags) {
        this.editingTemplate.tags = this.contact.tags
      } else {
        this.editingTemplate.tags = []
      }
    }
  },
  methods: {
    loadFormTemplates () {
      this.searching = true
      api.formtemplate.formtemplate.list({ search: this.search, count: 15 })
        .then(data => {
          this.searching = false
          this.formTemplates = data.results
        })
    },
    addUserForm () {
      this.propDialog = false
      this.copyTags = false
      api.userform.contact.addUserForm(this.contact, this.newFormTemplate, this.props.party_name, this.props.form_number, this.editingTemplate.tags)
        .then(contact => {
          this.newFormTemplate = ''
          this.editingTemplate = {}
          this.props = {
            party_name: '',
            form_number: ''
          }
          this.$emit('fetch')
        })
    },
    updateUserForm () {
      this.propDialog = false
      this.$emit('update:loading', true)
      const userFormUpdate = {
        id: this.editingTemplate.id,
        party_name: this.props.party_name,
        form_number: this.props.form_number,
        tags: this.editingTemplate.tags
      }
      this.copyTags = false
      api.userform.userForm.save(userFormUpdate)
        .then(() => {
          this.editingTemplate = {}
          this.props = {
            party_name: '',
            form_number: ''
          }
          this.$emit('fetch')
        })
    },
    editProps (template) {
      this.propDialog = true
      this.props.party_name = template.party_name
      this.props.form_number = template.form_number
      this.editingTemplate = JSON.parse(JSON.stringify(template))
    },
    fetchTags () {
      api.userform.contactTags.get(this.tagSearch)
        .then(tagResults => {
          this.tagResults = tagResults
        })
    },
  }
}
</script>
